<template>
  <div class="modern-master-view pb-12">
    <!-- Premium Global Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-file-invoice-dollar</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Facturación</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Gestión de documentos tributarios y cobranza</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center gap-3">
        <export-invoices :filter="filter" :options="options" :disabled="loading"/>
        <Can I="create" a="Invoice">
          <v-btn 
            color="success" 
            class="font-weight-black text-uppercase tracking-widest rounded-lg px-8 h-44 shadow-sm" 
            @click="$router.push({name:'CreateDTE',params:{id:-1,tipo:30}})"
          >
            <v-icon left small>fas fa-plus</v-icon> Emitir DTE
          </v-btn>
        </Can>
      </div>
    </v-row>

    <!-- Metrics Cards Section -->
    <v-row class="mb-8 px-4">
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card pa-6 d-flex align-center border-left-primary">
          <v-avatar color="primary lighten-5" size="48" class="mr-4">
            <v-icon color="primary">fas fa-calculator</v-icon>
          </v-avatar>
          <div>
            <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Total Facturado (c/ IVA)</div>
            <div class="text-h5 font-weight-black primary--text">{{ total | moneda }}</div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card pa-6 d-flex align-center border-left-success">
          <v-avatar color="success lighten-5" size="48" class="mr-4">
            <v-icon color="success">fas fa-hand-holding-usd</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Total Percibido / Pagado</div>
            <div class="d-flex align-center justify-space-between">
              <div class="text-h5 font-weight-black success--text">{{ payed | moneda }}</div>
              <v-chip x-small color="success lighten-4" class="success--text font-weight-black px-2">
                {{ (payed / total) | percent }} del total
              </v-chip>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Section -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense align="center">
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Desde</div>
          <v-text-field 
            v-model='filter.created_after' 
            outlined dense hide-details clearable type="date"
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Hasta</div>
          <v-text-field 
            v-model='filter.created_before' 
            outlined dense hide-details clearable type="date"
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Tipo</div>
          <v-select
            v-model="filter.tipo"
            :items="[{value:30, text:'Factura'}, {value:60, text:'Nota de Crédito'}]"
            item-text="text"
            item-value="value"
            placeholder="Todos"
            outlined dense hide-details clearable
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Vendedor</div>
          <v-select
            v-model="filter.seller"
            :items="sellers"
            item-text="email"
            item-value="id"
            placeholder="Todos"
            outlined dense hide-details clearable
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Estatus</div>
          <v-select
            v-model="filter.not_null"
            :items="[{'text':'Emitido','value':true}]"
            item-text="text"
            item-value="value"
            placeholder="Todos"
            outlined dense hide-details clearable
            class="modern-input"
            @change="()=>{ options.page=1; fetch() }"
          />
        </v-col>
        <v-col cols="12" md="auto" class="d-flex align-end pb-1 ml-auto">
          <v-btn color="primary" icon class="grey lighten-4" @click="fetch" :loading="loading">
            <v-icon small>fas fa-sync</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Table Card -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="computedHeaders"
        :items="dtes"
        :options.sync="options"
        :server-items-length="totalDtes"
        :loading="loading"
        class="modern-table"
        :item-class="(item)=>{return item.tipo==60?'row-style-nc':'row-style-normal'}"
        @click:row='displayDetails'
        @update:options="fetch"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Integrated Search Slot -->
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Documentos Emitidos: {{totalDtes}}</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              @keyup="searchTimeOut"
              placeholder="Buscar por folio, RUT o nombre..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details clearable
              :disabled="loading"
              class="modern-search-field"
            ></v-text-field>
          </div>
        </template>

        <!-- Cell Templates -->
        <template v-slot:item.document_id="{ item }">
          <div class="d-flex flex-column py-2">
            <div class="d-flex align-center mb-1">
              <span class="body-2 font-weight-black primary--text lh-1 mr-2">#{{ item.document_id }}</span>
              <v-chip v-if="item.tipo==60" x-small color="error lighten-5" class="error--text font-weight-black px-1" label>NC</v-chip>
            </div>
            <v-chip x-small :color="getStatusColor(item) + ' lighten-5'" :class="getStatusColor(item) + '--text font-weight-black'" label>
              {{ getStatusName(item) }}
            </v-chip>
          </div>
        </template>

        <template v-slot:item.date_emited="{ item }">
          <span class="body-2 grey--text text--darken-3 font-weight-medium">{{ item.date_emited | moment('DD/MM/YYYY') }}</span>
        </template>

        <template v-slot:item.payment_date="{ item }">
          <span class="caption grey--text font-weight-bold">{{ item.payment_date | moment('DD/MM/YYYY') }}</span>
        </template>

        <template v-slot:item.rznSocRecep="{ item }">
          <div class="d-flex flex-column py-2">
            <span class="body-2 font-weight-bold grey--text text--darken-4 lh-1-2 mb-1">{{ item.rznSocRecep }}</span>
            <span class="caption grey--text font-weight-black opacity-60">{{ item.rutRecep }}</span>
          </div>
        </template>

        <template v-slot:item.work_order="{ item }">
          <v-chip v-if="item.work_order" x-small outlined color="primary" class="font-weight-black">OT #{{ item.work_order }}</v-chip>
          <span v-else class="caption grey--text opacity-40">-</span>
        </template>

        <template v-slot:item.sales_note="{ item }">
          <span v-if="item.sales_note" class="caption grey--text font-weight-bold">CN #{{ item.sales_note }}</span>
          <span v-else class="caption grey--text opacity-40">-</span>
        </template>

        <template v-slot:item.get_total="{ item }">
          <span class="body-1 font-weight-black primary--text tracking-tighter">{{ item.get_total | moneda }}</span>
        </template>

        <template v-slot:item.payed="{ item }">
          <div v-if="item.tipo==60" class="text-center caption grey--text">-</div>
          <div v-else class="d-flex flex-column align-end">
            <span :class="(item.get_total - item.get_payed) > 0 ? 'error--text' : 'success--text'" class="body-2 font-weight-black">
              {{ item.get_payed | moneda }}
            </span>
            <span v-if="(item.get_total - item.get_payed) > 0" class="caption grey--text opacity-60">Pend: {{ (item.get_total - item.get_payed) | moneda }}</span>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-menu bottom left offset-y transition="slide-y-transition" content-class="modern-card">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" class="grey lighten-4" @click.stop>
                <v-icon small color="grey darken-2">fas fa-ellipsis-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense class="py-1">
              <Can I="update" a="Invoice">
                <v-list-item :disabled="item.tipo==60" @click.stop="()=>{ itemToPayment=item; toPayment=true; }">
                  <v-list-item-icon class="mr-2">
                    <v-icon x-small color="success">fas fa-money-check-alt</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-bold">Registrar Pago</v-list-item-title>
                </v-list-item>
              </Can>
              
              <v-divider class="my-1"></v-divider>

              <Can I="update" a="Invoice">
                <v-list-item v-if="Boolean(item.estatus<2)" @click.stop="()=>{ invoice=item; showConfirmReceived=true; }">
                  <v-list-item-icon class="mr-2">
                    <v-icon x-small color="primary">fas fa-sync</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-bold">Recepcionar</v-list-item-title>
                </v-list-item>
              </Can>

              <Can I="delete" a="Invoice">
                <v-list-item v-if="Boolean(item.estatus<3)" @click.stop="()=>{ invoice=item; showConfirmCancel=true; }" class="error--text">
                  <v-list-item-icon class="mr-2">
                    <v-icon x-small color="error">fas fa-trash</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="caption font-weight-bold">Anular Factura</v-list-item-title>
                </v-list-item>
              </Can>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- Dialogs -->
    <confirm
      v-if="invoice"
      v-model="showConfirmCancel" 
      :message="`¿Confirma la anulación de la Factura #${invoice.document_id}?`" 
      @ok="updateEstatus(3)"
      @cancel="showConfirmCancel=false"
    />
    <confirm
      v-if="invoice"
      v-model="showConfirmReceived" 
      :message="`¿Confirma el cambio de Estatus de la Factura #${invoice.document_id} a Recepcionada?`" 
      @ok="updateEstatus(2)"
      @cancel="showConfirmReceived=false"
    />
    
    <v-dialog v-model="showDetail" max-width="1200" content-class="modern-dialog">
      <v-card class="overflow-hidden rounded-xl bg-white">
        <invoice-detail :invoice="detailToDisplay" @close="showDetail=false"/>
      </v-card>
    </v-dialog>

    <v-dialog v-model="toPayment" max-width="900" content-class="modern-dialog" persistent>
      <v-card class="overflow-hidden rounded-xl bg-white">
        <div class="pa-8">
          <div class="d-flex align-center mb-6">
            <v-avatar color="success lighten-5" size="40" class="mr-4">
              <v-icon color="success" size="18">fas fa-money-bill-wave</v-icon>
            </v-avatar>
            <h2 class="text-h5 font-weight-black primary--text">Historial de Pagos - Factura #{{ itemToPayment ? itemToPayment.document_id : '' }}</h2>
            <v-spacer></v-spacer>
            <v-btn icon @click="toPayment=false"><v-icon>fas fa-times</v-icon></v-btn>
          </div>
          <invoice-pay-detail :invoice="itemToPayment" @refresh="()=>{ toPayment=false; fetch(); }"/>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { fetchDTE, updateDTE } from '@/api/salesNotes'
import { fetchSellers } from '@/api/auth'
import notifications from '@/utils/notifications'
import ExportInvoices from '@/components/sales/ExportInvoices'
import InvoicePayDetail from '@/components/sales/InvoicePayDetail'
import InvoiceDetail from '@/components/sales/InvoiceDetail'
import moment from 'moment'

export default {
  name: 'Invoices',
  components: { ExportInvoices, InvoicePayDetail, InvoiceDetail },
  data() {
    return {
      loading: false,
      showDetail: false,
      detailToDisplay: '',
      dtes: [],
      sellers: [],
      invoice: '',
      timer: null,
      options: {},
      totalDtes: 0,
      showConfirmCancel: false,
      showConfirmReceived: false,
      toPayment: false,
      itemToPayment: null,
      search: '',
      total: 0,
      payed: 0,
      estatuses: [
        { value: 1, text: 'Emitido', color: 'info' },
        { value: 2, text: 'Recepcionado', color: 'success' },
        { value: 3, text: 'Anulado', color: 'error' },
      ],
      filter: {
        created_after: moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('yyyy-MM-DD'),
        created_before: '',
        tipo: '',
        seller: '',
        not_null: 'true',
      },
      headers: [
        { text: 'Documento', align: 'start', value: 'document_id', class: 'table-header', width: '140px' },
        { text: 'Emisión', align: 'start', value: 'date_emited', class: 'table-header', width: '120px' },
        { text: 'Vence', align: 'start', value: 'payment_date', class: 'table-header', width: '120px' },
        { text: 'Razón Social / Cliente', align: 'start', value: 'rznSocRecep', class: 'table-header' },
        { text: 'OT', align: 'start', value: 'work_order', class: 'table-header', sortable: true, width: '110px' },
        { text: 'Cot.', align: 'start', value: 'sales_note', class: 'table-header', sortable: false, width: '100px' },
        { text: 'Total Bruto', value: 'get_total', class: 'table-header', align: 'end', sortable: false, width: '140px' },
        { text: 'Pagado', value: 'payed', class: 'table-header', align: 'end', sortable: false, width: '140px' },
        { text: '', align: 'end', value: 'actions', class: 'table-header', sortable: false, width: '60px' },
      ],
    }
  },
  methods: {
    getStatusName(item) {
      const status = this.estatuses.find(i => i.value == item.estatus)
      return status ? status.text : 'Desconocido'
    },
    getStatusColor(item) {
      const status = this.estatuses.find(i => i.value == item.estatus)
      return status ? status.color : 'grey'
    },
    displayDetails(item) {
      this.detailToDisplay = item
      this.showDetail = true
    },
    async fetch() {
      this.loading = true
      const { sortBy, sortDesc, page, itemsPerPage } = this.options
      let sort = (sortBy && sortBy.length) ? sortBy[0] : '-date_emited'
      
      // Permisos de filtrado
      let sellerFilter = this.filter.seller
      if (!this.$can('readAll', 'SalesNote')) {
        sellerFilter = this.$store.getters.getUser.id
      }

      const query = `ordering=${sortDesc && sortDesc[0] ? sort : '-' + sort}&page=${page || 1}&page_size=${itemsPerPage || 20}&search=${this.search}&tipo=${this.filter.tipo || ''}&emited_after=${this.filter.created_after || ''}&emited_before=${this.filter.created_before || ''}&customer__seller__id=${sellerFilter || ''}&not_null=${this.filter.not_null || ''}`
      
      try {
        const res = await fetchDTE(query)
        this.totalDtes = res.count
        this.dtes = res.results
        this.total = res.total
        this.payed = res.payed
      } catch (err) {
        notifications.showError('Error cargando facturas')
      } finally {
        this.loading = false
      }
    },
    searchTimeOut() {  
      if (this.timer) clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.options.page = 1
        this.fetch()
      }, 800)
    },
    async updateEstatus(estatus) {
      try {
        const res = await updateDTE({ id: this.invoice.id, estatus: estatus })
        this.showConfirmCancel = false
        this.showConfirmReceived = false
        this.invoice = ''
        notifications.showSuccess(`Documento #${res.document_id} actualizado con éxito`)
        this.fetch()
      } catch (err) {
        notifications.showError('Error al actualizar el estatus')
      }
    },
  },
  async mounted() {
    this.fetch()
    this.sellers = await fetchSellers()
  },
  computed: {
    computedHeaders() { return this.headers }
  }
}
</script>

<style scoped>
.modern-master-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
.border-left-success { border-left: 4px solid var(--v-success-base) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -0.5px; }
.h-44 { height: 44px !important; }
.opacity-60 { opacity: 0.6; }

.modern-input >>> fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-search-field { width: 320px; }
.modern-search-field >>> fieldset { border-radius: 24px !important; border-color: #e0e4e8 !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 56px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 72px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table >>> tr:hover { background-color: #f4f7f9 !important; cursor: pointer; }

.row-style-nc { background-color: #fffbfa !important; }
.row-style-nc td { color: #d32f2f !important; }
</style>